import Image from 'next/image';

import { FC } from 'react';
import { Theme } from './BlogHeader.consts';
import { useBlogHeader } from './BlogHeader.hooks';
import {
  BreadcrumbArrowIcon,
  ChatGptIcon,
  ClaudeIcon,
  CopyLinkIcon,
  EmailIcon,
  GooglePreferredSourceIcon,
  LinkedInIcon,
} from './BlogHeader.icons';


type HeroImageSize = 'large' | 'small';

const backgroundClassNames = {
  violet: 'bg-violet-800',
  light: 'bg-violet-100',
  green: 'bg-green-800',
  dark: 'bg-blue-900',
  magenta: 'bg-pink-900',
} as const satisfies Record<Theme, string>

const textClassNames = {
  violet: 'text-white',
  light: 'text-blue-900',
  green: 'text-white',
  dark: 'text-white',
  magenta: 'text-white',
} as const satisfies Record<Theme, string>

const lightTextClassNames = {
  violet: 'text-violet-300',
  light: 'text-gray-600',
  green: 'text-green-300',
  dark: 'text-blue-300',
  magenta: 'text-pink-300',
} as const satisfies Record<Theme, string>

const borderClassNames = {
  violet: 'border-violet-700',
  light: 'border-violet-300',
  green: 'border-green-700',
  dark: 'border-blue-800',
  magenta: 'border-pink-700',
} as const satisfies Record<Theme, string>

const iconHoverClassNames = {
  violet: 'hover:text-white',
  light: 'hover:text-blue-900',
  green: 'hover:text-white',
  dark: 'hover:text-white',
  magenta: 'hover:text-white',
} as const satisfies Record<Theme, string>

export type BlogHeaderProps = {
  theme?: Theme;
  heroImage?: string;
  heroImageSize?: HeroImageSize;
  breadcrumbs: Array<{ label: string; href?: string }>;
  title: string;
  author: {
    name: string;
    role: string;
    imageSrc?: string;
    imageAlt?: string;
  };
  date: string;
  readTime: string;
  className?: string;
  pageUrl: string;
  emailBody?: string;
}


const BlogHeader: FC<BlogHeaderProps> = ({
  theme = 'violet',
  heroImage,
  heroImageSize = 'small',
  breadcrumbs,
  title,
  author,
  date,
  readTime,
  className,
  pageUrl,
  emailBody,
}) => {

  const {
    onEmailIconClick,
    onCopyLinkIconClick,
    onLinkedInIconClick,
    onChatGptIconClick,
    onClaudeIconClick,
    onGoogleSourceIconClick,
    showPopup,
    linkedInShareLink,
    chatGptShareLink,
    claudeShareLink,
    googleSourceLink,
  } = useBlogHeader({ pageUrl, title });

  const backgroundClass = backgroundClassNames[theme];

  const textClass = textClassNames[theme];

  const lightTextClass = lightTextClassNames[theme];

  const borderClass = borderClassNames[theme];

  const iconHoverClass = iconHoverClassNames[theme];


  return (
    <header className={`${backgroundClass} ${textClass} ${className}`}>

      <section className={`${heroImage && heroImageSize != "large" ? 'md:grid md:grid-cols-[minmax(0,_1fr)_minmax(0,_1fr)] gap-x-gutter-mobile md:gap-x-gutter-desktop' : ''} max-md:px-container-mobile mx-auto`}>

        <div className={`${heroImage && heroImageSize == 'small' ? 'md:pb-8' : 'md:pb-5'} pt-4 pb-4 md:pl-container-desktop flex flex-col justify-between`}>
          <p className={`${lightTextClass} text-micro2 uppercase tracking-wide flex items-center gap-1 mb-4}`}>
            {breadcrumbs.map((crumb, index) => (
              <span key={index} className="flex items-center gap-1">
                <a href={crumb.href || '#'} className="hover:underline">{crumb.label}</a>
                {index < breadcrumbs.length - 1 && (
                  <BreadcrumbArrowIcon className={lightTextClass} />
                )}
              </span>
            ))}
          </p>

          <h1 className={`${heroImage && heroImageSize == 'small' ? '' : 'max-w-[32em]'} ${heroImage && heroImageSize == 'large' ? 'md:mt-2' : 'md:mt-8'} text-h3 xl:text-display3 leading-tight mt-4 pr-4`}>
            {title}
          </h1>
        </div>

        {heroImage && (
          <div className={`${borderClass} ${heroImageSize != 'large' ? 'md:w-full h-full' : 'md:px-container-desktop'} relative max-md:aspect-video flex items-center justify-center max-md:-mx-container-mobile max-md:w-[calc(100%+48px)] md:mx-0 md:w-full border-solid md:border-l max-md:border-t overflow-hidden`}>
            {heroImageSize != 'large' && (
              <div className="bg-citron-500 absolute left-0 top-0 right-0 bottom-0 animate-blog-header-fill-in"></div>
            )}
            <Image className={`${heroImageSize != 'large' ? 'opacity-0 animate-blog-header-img-in' : ''} max-md:aspect-video min-h-[100%] object-cover w-full max-w-full h-auto`} src={heroImage} alt="" width={"1600"} height={"800"} />
          </div>
        )}
      </section>

      <div className={`${borderClass} border-solid border-t`}>

        <div className="md:mx-auto grid grid-cols-2 md:grid-cols-[calc(50%+12px)_calc(50%-12px)]  xl:grid-cols-[minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_2fr)] xl:gap-x-[12px] xl:px-container-desktop">

          <div className="min-xl:px-container-mobile flex flex-col md:flex-row md:items-center md:justify-between w-full max-xl:col-span-2">

            <div className="py-2 max-md:px-container-mobile  max-xl:px-container-desktop md:py-4 flex items-center gap-2 md:pr-8">
              {author.imageSrc && (
                <Image src={author.imageSrc} width={"48"} height={"48"} alt={author.imageAlt || author.name} className="w-6 h-6 rounded-full bg-gray-500/40 object-cover" />
              )}
              <div>
                <p className="text-caption1 leading-none">{author.name}</p>
                <p className={`${lightTextClass} text-caption1 leading-none`}>{author.role}</p>
              </div>
            </div>

          </div>

          <div className={`${borderClass} max-md:px-container-mobile max-xl:px-container-desktop max-xl:py-2 border-solid max-xl:border-t max-xl:border-r text-center ${lightTextClass} text-micro2 uppercase tracking-wide uppercaseflex items-center gap-1 flex xl:justify-center xl:border-l xl:-mr-[12px]`}>
            <time dateTime={date}>{date}</time>
            {readTime && (
              <>
                <span aria-hidden="true" className="max-sm:hidden">•</span>
                <span className="max-sm:hidden">{readTime}</span>
              </>
            )}
          </div>

          <div className={`${borderClass} max-md:px-container-mobile max-xl:px-container-desktop max-xl:py-2 flex flex-wrap lg:flex-nowrap items-center justify-start lg:justify-end gap-2 text-white/80 w-full border-solid max-xl:border-t xl:border-l relative`}>

            <a
              href={chatGptShareLink}
              aria-label="Summarize with ChatGPT"
              className="group inline-flex shrink-0"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onChatGptIconClick}
            >
              <ChatGptIcon className={`${lightTextClass} ${iconHoverClass} transition-colors shrink-0 w-[20px] h-[20px]`} />
            </a>

            <a
              href={claudeShareLink}
              aria-label="Summarize with Claude"
              className="group inline-flex shrink-0"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClaudeIconClick}
            >
              <ClaudeIcon className={`${lightTextClass} ${iconHoverClass} transition-colors shrink-0 w-[20px] h-[20px]`} />
            </a>

            {linkedInShareLink && <a
              href={linkedInShareLink}
              aria-label="Share on LinkedIn"
              className="group"
              onClick={onLinkedInIconClick}
            >
              <LinkedInIcon className={`${lightTextClass} ${iconHoverClass}`} />
            </a>}

            <a
              href={`mailto:?body=${encodeURIComponent(emailBody ?? '')}`}
              aria-label="Share via email"
              className="group"
              onClick={onEmailIconClick}
            >
              <EmailIcon className={`${lightTextClass} ${iconHoverClass}`} />
            </a>

            <a
              href="#copy-link"
              aria-label="Copy link"
              className="group relative"
              onClick={onCopyLinkIconClick}
            >
              <CopyLinkIcon className={`${lightTextClass} ${iconHoverClass}`} />
              {showPopup && (
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs bg-green-100 text-green-800 border border-green-800 rounded px-2 py-1">
                  Link copied!
                </span>
              )}
            </a>

            <a
              href={googleSourceLink}
              aria-label="Set as preferred Google news source"
              className="group inline-flex shrink-0"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onGoogleSourceIconClick}
            >
              <GooglePreferredSourceIcon className={`${lightTextClass} ${iconHoverClass} transition-colors shrink-0 h-[30px] w-auto`} />
            </a>

          </div>
        </div>

      </div>
    </header >
  );
}

export default BlogHeader;
